import React, { useMemo, useCallback, Suspense, lazy } from 'react';
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import { WalletError } from '@solana/wallet-adapter-base';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { BridgeInterface } from './components/BridgeInterface';
import { Header } from './components/Header';
import { StatusBar } from './components/StatusBar';
import { DocsPage } from './components/DocsPage';
import { MINT_PAGES_ENABLED } from './config/launch';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

import '@solana/wallet-adapter-react-ui/styles.css';
import { PhantomProvider } from './context/PhantomContext';

// DUSD and CR-Stable. Their API endpoints answer 410 Gone, so the pages are
// off by default and only reachable when VITE_ENABLE_MINT_PAGES says so.
// Lazy so a build with them disabled never loads their chunks; the components
// themselves are untouched, and flipping the flag brings them straight back.
// With the routes gone, /mint-dusd and /mint-crs fall through to the
// catch-all and land on the bridge rather than on nothing.
const MintDUSD = lazy(() =>
  import('./components/MintDUSD').then((m) => ({ default: m.MintDUSD })),
);
const MintCRStable = lazy(() =>
  import('./components/MintCRStable').then((m) => ({ default: m.MintCRStable })),
);

const SOLANA_RPC = import.meta.env.VITE_SOLANA_RPC_URL || 'http://127.0.0.1:8899';

function App() {
  // Don't manually add PhantomWalletAdapter — Wallet Standard auto-detects Phantom.
  // Adding both causes duplicate registrations and identity mismatches.
  const wallets = useMemo(() => [], []);

  const onError = useCallback((error: WalletError) => {
    console.error('[wallet]', error);
    toast.error(error.message || 'Wallet connection failed');
  }, []);

  return (
    <ConnectionProvider endpoint={SOLANA_RPC}>
      <WalletProvider wallets={wallets} localStorageKey="walletName_bridge" autoConnect={false} onError={onError}>
        <PhantomProvider>
          <BrowserRouter>
            <Suspense fallback={null}>
            <Routes>
              {/* Docs page — standalone layout */}
              <Route path="/docs" element={<DocsPage />} />

              {/* Mint DUSD page */}
              {MINT_PAGES_ENABLED && (
              <Route
                path="/mint-dusd"
                element={
                  <div className="min-h-screen bg-gray-950 relative overflow-hidden">
                    <div className="pointer-events-none fixed inset-0 z-0">
                      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-900/10 blur-[120px]" />
                      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-cyan-900/10 blur-[100px]" />
                    </div>
                    <Header />
                    <main className="relative z-10 max-w-xl mx-auto px-4 py-8 pb-16">
                      <MintDUSD />
                    </main>
                    <StatusBar />
                    <Toaster
                      position="bottom-right"
                      toastOptions={{
                        style: {
                          background: '#1f2937',
                          color: '#fff',
                          border: '1px solid #374151',
                        },
                      }}
                    />
                  </div>
                }
              />
              )}

              {/* Mint CR Stable page */}
              {MINT_PAGES_ENABLED && (
              <Route
                path="/mint-crs"
                element={
                  <div className="min-h-screen bg-gray-950 relative overflow-hidden">
                    <div className="pointer-events-none fixed inset-0 z-0">
                      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-900/10 blur-[120px]" />
                      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-teal-900/10 blur-[100px]" />
                    </div>
                    <Header />
                    <main className="relative z-10 max-w-xl mx-auto px-4 py-8 pb-16">
                      <MintCRStable />
                    </main>
                    <StatusBar />
                    <Toaster
                      position="bottom-right"
                      toastOptions={{
                        style: {
                          background: '#1f2937',
                          color: '#fff',
                          border: '1px solid #374151',
                        },
                      }}
                    />
                  </div>
                }
              />
              )}

              {/* Main bridge app */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen bg-gray-950 relative overflow-hidden">
                    {/* Ambient background glow */}
                    <div className="pointer-events-none fixed inset-0 z-0">
                      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-purple-900/10 blur-[120px]" />
                      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-indigo-900/10 blur-[100px]" />
                    </div>
                    <Header />
                    <main className="relative z-10 max-w-xl mx-auto px-4 py-8 pb-16">
                      <BridgeInterface />
                    </main>
                    <StatusBar />
                    <Toaster
                      position="bottom-right"
                      toastOptions={{
                        style: {
                          background: '#1f2937',
                          color: '#fff',
                          border: '1px solid #374151',
                        },
                      }}
                    />
                  </div>
                }
              />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </PhantomProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export default App;
